import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_OG_IMAGE,
  getBreadcrumbs,
  getCanonicalUrl,
  SITE_NAME,
} from "@/lib/seo";

interface PageSeoProps {
  title: string;
  description: string;
  breadcrumbLabel?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  noIndex?: boolean;
  includeCanonical?: boolean;
}

const PageSeo = ({
  title,
  description,
  breadcrumbLabel,
  canonicalPath,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  noIndex = false,
  includeCanonical = true,
}: PageSeoProps) => {
  const { pathname } = useLocation();
  const canonical = getCanonicalUrl(canonicalPath ?? pathname);
  const breadcrumbs = getBreadcrumbs(canonicalPath ?? pathname, breadcrumbLabel);

  // Keep search snippets inside the usual display limits without changing the
  // full headings and descriptions rendered on the page.
  const safeTitle = title.length > 60 ? `${title.substring(0, 57)}...` : title;
  const safeDescription = description.length > 183
    ? `${description.substring(0, 180)}...`
    : description;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${canonical}#breadcrumb`,
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: getCanonicalUrl(item.path),
    })),
  };

  return (
    <Helmet>
      <html lang="en-ET" />
      <title>{safeTitle}</title>
      <meta name="description" content={safeDescription} />
      <meta
        name="keywords"
        content="eSchool, school management system, student management, classroom management, fee management, Addis Ababa, Ethiopia, education software, cloud-based school system"
      />
      <meta name="robots" content={noIndex ? "noindex, follow" : "index, follow"} />
      <meta name="X-Robots-Tag" content={noIndex ? "noindex, follow" : "index, follow"} />
      <link rel="publisher" href="https://eschool.et" />

      {includeCanonical && <link rel="canonical" href={canonical} />}
      {includeCanonical && !noIndex && (
        <link rel="alternate" hrefLang="en-ET" href={canonical} />
      )}
      {includeCanonical && !noIndex && (
        <link rel="alternate" hrefLang="x-default" href={canonical} />
      )}

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {includeCanonical && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_ET" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {!noIndex && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default PageSeo;

